import React, { useState, useRef } from 'react';
import { FaUpload, FaTrashAlt, FaEye, FaDownload, FaFilePdf, FaFileWord } from 'react-icons/fa';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import { useTranslation } from 'react-i18next';
import * as api from '../../../../api/apiConnector.js';
import * as uploadManager from '../../../../components/ui/fileupload.js';

/**
 * UserGuideManager Component
 * 
 * Manages user guide file upload, preview, and deletion for form builder
 * Allows admins to upload PDF/Word documents that serve as user guides during form compilation
 */
const UserGuideManager = ({ 
  form, 
  currentUser, 
  colors, 
  onGuideFileUpdate, 
  disabled = false 
}) => {
  const { t } = useTranslation();
  const MySwal = withReactContent(Swal);
  const fileInputRef = useRef(null);
  
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(false);

  // Get current guide file from form data (first in array, consistent with questions pattern)
  const currentGuideFile = form?.userGuideFiles?.[0] || null;

  /**
   * Get appropriate icon for file type
   */
  const getFileIcon = (fileName) => {
    const extension = fileName?.split('.').pop()?.toLowerCase();
    switch (extension) {
      case 'pdf':
        return <FaFilePdf className="text-danger" />;
      case 'doc':
      case 'docx':
        return <FaFileWord className="text-primary" />;
      default:
        return <FaEye />;
    }
  };

  /**
   * Format file size in human readable format
   */
  const formatFileSize = (bytes) => {
    if (!bytes) return '';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  /**
   * Handle file selection from input
   */
  const handleFileSelect = async (event) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    const file = files[0];
    
    // Validate file type
    const allowedTypes = [
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
    ];
    
    if (!allowedTypes.includes(file.type)) {
      MySwal.fire({
        title: t('error', { ns: 'labels' }),
        text: t('invalidFileType', { ns: 'labels', defaultValue: 'Tipo di file non valido. Sono supportati solo PDF e documenti Word.' }),
        icon: 'error',
        confirmButtonColor: colors?.danger || '#dc3545'
      });
      return;
    }

    // Validate file size (max 10MB)
    const maxSize = 10 * 1024 * 1024; // 10MB
    if (file.size > maxSize) {
      MySwal.fire({
        title: t('error', { ns: 'labels' }),
        text: t('fileTooLarge', { ns: 'labels', defaultValue: 'Il file è troppo grande. Dimensione massima: 10MB.' }),
        icon: 'error',
        confirmButtonColor: colors?.danger || '#dc3545'
      });
      return;
    }

    await uploadGuideFile(file);
  };

  /**
   * Upload guide file to server using uploadManager (same pattern as questions)
   */
  const uploadGuideFile = async (file) => {
    if (!form?.Id) {
      MySwal.fire({
        title: t('error', { ns: 'labels' }),
        text: t('saveFormFirst', { ns: 'labels', defaultValue: 'Salva prima il form per poter caricare la guida utente.' }),
        icon: 'warning',
        confirmButtonColor: colors?.warning || '#ffc107'
      });
      return;
    }

    setUploading(true);

    try {
      // Use uploadManager like questions do (same pattern)
      uploadManager.fileUpload({
        user: currentUser(),
        parentId: 'formUserGuides', // Dedicated folder for user guides
        files: [file],
        onFileAdded: (files) => {
          if (Array.isArray(files) && files.length > 0) {
            const uploadedFile = files[0];
            
            // Update form with userGuideFiles array (consistent with questions)
            // But we'll only use the first file for display
            onGuideFileUpdate([uploadedFile]);
            
            MySwal.fire({
              title: t('success', { ns: 'labels' }),
              text: t('guideUploadedSuccessfully', { ns: 'labels', defaultValue: 'Guida utente caricata con successo.' }),
              icon: 'success',
              timer: 2000,
              showConfirmButton: false
            });
          }
        },
        onError: (error) => {
          console.error('Upload error:', error);
          MySwal.fire({
            title: t('error', { ns: 'labels' }),
            text: error.message || t('uploadFailed', { ns: 'labels', defaultValue: 'Caricamento fallito.' }),
            icon: 'error',
            confirmButtonColor: colors?.danger || '#dc3545'
          });
        }
      });
    } catch (error) {
      console.error('Error uploading guide file:', error);
      MySwal.fire({
        title: t('error', { ns: 'labels' }),
        text: error.message || t('uploadFailed', { ns: 'labels', defaultValue: 'Caricamento fallito.' }),
        icon: 'error',
        confirmButtonColor: colors?.danger || '#dc3545'
      });
    } finally {
      setUploading(false);
      // Clear file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  /**
   * Handle guide file deletion using DMS API (same pattern as questions)
   */
  const handleDeleteGuideFile = async () => {
    if (!currentGuideFile?.Id) return;

    // Confirm deletion
    const result = await MySwal.fire({
      title: t('attention', { ns: 'labels' }),
      text: t('confirmDeleteGuide', { ns: 'labels', defaultValue: 'Sei sicuro di voler eliminare la guida utente?' }),
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: colors?.danger || '#dc3545',
      cancelButtonColor: colors?.secondary || '#6c757d',
      confirmButtonText: t('delete', { ns: 'labels', defaultValue: 'Elimina' }),
      cancelButtonText: t('cancel', { ns: 'labels', defaultValue: 'Annulla' })
    });

    if (!result.isConfirmed) return;

    setLoading(true);

    try {
      MySwal.fire({
        title: t('deleting', { ns: 'labels', defaultValue: 'Eliminazione...' }),
        allowOutsideClick: false,
        showConfirmButton: false,
        willOpen: () => {
          Swal.showLoading();
        }
      });

      // Use DMS API to delete file (correct endpoint from dms_screen.js)
      const deleteResult = await api.sendMessage("dms/deleteFile", {
        user: currentUser(),
        body: {
          id: currentGuideFile.Id
        }
      });

      if (deleteResult.success) {
        // Clear userGuideFiles array
        onGuideFileUpdate([]);
        MySwal.fire({
          title: t('success', { ns: 'labels' }),
          text: t('guideDeletedSuccessfully', { ns: 'labels', defaultValue: 'Guida utente eliminata con successo.' }),
          icon: 'success',
          timer: 2000,
          showConfirmButton: false
        });
      } else {
        throw new Error(deleteResult.error || t('deleteFailed', { ns: 'labels', defaultValue: 'Eliminazione fallita.' }));
      }
    } catch (error) {
      console.error('Error deleting guide file:', error);
      MySwal.fire({
        title: t('error', { ns: 'labels' }),
        text: error.message || t('deleteFailed', { ns: 'labels', defaultValue: 'Eliminazione fallita.' }),
        icon: 'error',
        confirmButtonColor: colors?.danger || '#dc3545'
      });
    } finally {
      setLoading(false);
    }
  };

  /**
   * Handle guide file preview/download using DMS API (same pattern as questions)
   * Note: UserGuideManager is only used in form builder mode, not in simulation
   */
  const handlePreviewGuideFile = async () => {
    if (!currentGuideFile?.Id) return;

    try {
      setLoading(true);
      
      // Use authenticated API download (same pattern as UserGuideDisplay.js)
      const blob = await api.downloadFileGet(`dms/downloadFile/get/${currentGuideFile.Id}`, {
        user: currentUser()
      });
      
      // Create blob URL and open in new window
      const blobUrl = window.URL.createObjectURL(blob);
      const newWindow = window.open(blobUrl, '_blank');
      
      // Clean up blob URL after the window loads (or after a delay)
      setTimeout(() => {
        window.URL.revokeObjectURL(blobUrl);
      }, 1000);
      
    } catch (error) {
      console.error('Error previewing guide file:', error);
      MySwal.fire({
        title: t('error', { ns: 'labels' }),
        text: t('previewFailed', { ns: 'labels', defaultValue: 'Impossibile visualizzare l\'anteprima.' }),
        icon: 'error',
        confirmButtonColor: colors?.danger || '#dc3545'
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="border rounded p-3" style={{ backgroundColor: colors?.light || '#f8f9fa' }}>
      <label className="form-label fw-bold mb-3">
        <i className="fa-solid fa-book-open me-2"></i>
        {t('userGuideFile', { ns: 'labels', defaultValue: 'File Guida Utente' })}
      </label>
      
      {currentGuideFile ? (
        // Show current guide file
        <div className="card">
          <div className="card-body p-3">
            <div className="d-flex align-items-center justify-content-between">
              <div className="d-flex align-items-center flex-grow-1">
                <div className="me-3">
                  {getFileIcon(currentGuideFile.Name)}
                </div>
                <div className="flex-grow-1">
                  <h6 className="mb-1">{currentGuideFile.Name}</h6>
                  <small className="text-muted">
                    {formatFileSize(currentGuideFile.Size)}
                    {currentGuideFile.ItemLastUpdated && (
                      <> • {t('uploaded', { ns: 'labels', defaultValue: 'Caricato' })}: {new Date(currentGuideFile.ItemLastUpdated).toLocaleDateString()}</>
                    )}
                  </small>
                </div>
              </div>
              
              <div className="d-flex gap-2">
                <button
                  type="button"
                  className="btn btn-sm btn-outline-primary"
                  onClick={handlePreviewGuideFile}
                  disabled={loading || disabled}
                  title={t('preview', { ns: 'labels', defaultValue: 'Anteprima' })}
                >
                  {loading ? (
                    <div className="spinner-border spinner-border-sm" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                  ) : (
                    <FaEye />
                  )}
                </button>
                
                <button
                  type="button"
                  className="btn btn-sm btn-outline-danger"
                  onClick={handleDeleteGuideFile}
                  disabled={loading || disabled}
                  title={t('delete', { ns: 'labels', defaultValue: 'Elimina' })}
                >
                  <FaTrashAlt />
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : (
        // Show upload area
        <div className="text-center py-4">
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileSelect}
            accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
            style={{ display: 'none' }}
            disabled={uploading || disabled}
          />
          
          <div className="mb-3">
            <i className="fa-solid fa-cloud-upload-alt fa-3x text-muted"></i>
          </div>
          
          <h6 className="text-muted mb-2">
            {t('uploadGuideFile', { ns: 'labels', defaultValue: 'Carica File Guida' })}
          </h6>
          
          <p className="text-muted small mb-3">
            {t('supportedFormats', { ns: 'labels', defaultValue: 'Formati supportati: PDF, DOC, DOCX' })}
            <br />
            {t('maxFileSize', { ns: 'labels', defaultValue: 'Dimensione massima: 10MB' })}
          </p>
          
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading || disabled}
          >
            {uploading ? (
              <>
                <div className="spinner-border spinner-border-sm me-2" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
                {t('uploading', { ns: 'labels', defaultValue: 'Caricamento...' })}
              </>
            ) : (
              <>
                <FaUpload className="me-2" />
                {t('selectFile', { ns: 'labels', defaultValue: 'Seleziona File' })}
              </>
            )}
          </button>
        </div>
      )}
      
      <div className="form-text text-muted mt-2">
        <i className="fa-solid fa-info-circle me-1"></i>
        {t('userGuideDescription', { ns: 'labels', defaultValue: 'Il file verrà mostrato agli utenti durante la compilazione del form come guida di riferimento.' })}
      </div>
    </div>
  );
};

export default UserGuideManager;
